import { Box, BoxProps } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { FC, Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SubjectLectures } from '@/components/ui/features/library/SubjectSection/components/SubjectLectures';
import { SubjectSectionHeader } from '@/components/ui/features/library/SubjectSection/components/SubjectSectionHeader';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import { LectureSort } from '@/enums/LectureSort';
import { useStores } from '@/hooks/useStores';
import { useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { fetchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryLecturesByQueryInfinite } from '@/queries/thin-lectures';
import { ThinLecture } from '@/resources/ThinLecture';
import { ThinSubject } from '@/resources/ThinSubject';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface ISubjectSectionProps extends BoxProps {
  subject: ThinSubject;
}

export const SubjectSection: FC<ISubjectSectionProps> = ({ subject, ...rest }) => {
  const { store } = useStores();
  const { t } = useTranslation();
  const { data, error, size, setSize } = useResourcesInfinite(
    subject?.id
      ? queryLecturesByQueryInfinite({
          filters: {
            scope: 'published',
            library: 'true',
            with_doi_content: 'true',
            subject_subtree_of_id: subject?.id,
          },
          include: ['sorted_profiles', 'event'],
          sort: `-${LectureSort.HeldAt}`,
          pageSize: 16,
        })
      : null,
    { sideload: fetchLectureAccessPermissionsSideload(store) }
  );

  const { meta, ...swrExtract } = useMemo(
    () => extractSWRInfiniteData<ThinLecture>(data as Array<Response<ThinLecture>>, error, size),
    [data, error, size]
  );

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  return (
    <Box {...rest}>
      <SubjectSectionHeader w="full" fontSize="14px" subject={subject} totalCount={meta?.totalCount} />
      {meta?.totalCount === 0 ? (
        <NoResults title={t('common.noResults')} />
      ) : (
        <Fragment>
          <SubjectLectures onLoadMore={onLoadMore} {...swrExtract} />
        </Fragment>
      )}
    </Box>
  );
};
